import type { AudienceFilterKey, AudienceFilters } from '../types';
import { isPurchaseLookbackWindow, PURCHASE_LOOKBACK_WINDOWS } from './filters';

export type FilterErrors = Partial<Record<AudienceFilterKey, string>>;

const RANGES: readonly { min: AudienceFilterKey; max: AudienceFilterKey; label: string }[] = [
  { min: 'ageMin', max: 'ageMax', label: 'age' },
  { min: 'incomeMin', max: 'incomeMax', label: 'income' },
  { min: 'orderCountMin', max: 'orderCountMax', label: 'order count' }
];

const NON_NEGATIVE_INTEGERS: readonly AudienceFilterKey[] = [
  'ageMin',
  'ageMax',
  'orderCountMin',
  'orderCountMax'
];

const POSITIVE_INTEGERS: readonly AudienceFilterKey[] = ['uniqueIndividualLimit'];

/** Shown on a purchase window outside `PURCHASE_LOOKBACK_WINDOWS`, listing the windows as "a, b, or c days". */
export const PURCHASE_WINDOW_ERROR = `Pick ${PURCHASE_LOOKBACK_WINDOWS.slice(0, -1).join(', ')}, or ${PURCHASE_LOOKBACK_WINDOWS.at(-1)} days`;

/**
 * Client-side checks that mirror the API's 400s, keyed by filter field.
 *
 * - An inverted range is reported on the max bound, as the API's `path` does.
 * - Counts and ages must be whole numbers at or above zero, and the size cap a whole number at or above one.
 * - The purchase window must be one of `PURCHASE_LOOKBACK_WINDOWS`.
 * - Income must be at or above zero.
 * - A brand or state cannot be both included and excluded.
 *
 * Returns an empty object when the filter set is valid.
 */
export const validateFilters = (filters: AudienceFilters): FilterErrors => {
  const errors: FilterErrors = {};

  NON_NEGATIVE_INTEGERS.forEach(key => {
    const value = numberAt(filters, key);
    if (value != null && (!Number.isInteger(value) || value < 0))
      errors[key] = 'Must be a whole number, 0 or more';
  });

  POSITIVE_INTEGERS.forEach(key => {
    const value = numberAt(filters, key);
    if (value != null && (!Number.isInteger(value) || value < 1))
      errors[key] = 'Must be a whole number, 1 or more';
  });

  const lookback = filters.purchaseLookbackDays;
  if (lookback != null && !isPurchaseLookbackWindow(lookback))
    errors.purchaseLookbackDays = PURCHASE_WINDOW_ERROR;

  (['incomeMin', 'incomeMax'] as const).forEach(key => {
    const value = filters[key];
    if (value != null && value < 0) errors[key] = 'Must be 0 or more';
  });

  RANGES.forEach(({ min, max, label }) => {
    const low = numberAt(filters, min);
    const high = numberAt(filters, max);
    if (low != null && high != null && low > high && !errors[max])
      errors[max] = `Max ${label} must be at least the min`;
  });

  const overlap = (a: string[], b: string[]) => a.filter(item => b.includes(item));
  const stateOverlap = overlap(filters.includedStates, filters.excludedStates);
  if (stateOverlap.length > 0)
    errors.excludedStates = `Both included and excluded: ${stateOverlap.join(', ')}`;
  const brandOverlap = overlap(filters.includedBrandIds, filters.excludedBrandIds);
  if (brandOverlap.length > 0)
    errors.excludedBrandIds = `Both included and excluded: ${brandOverlap.join(', ')}`;

  return errors;
};

export const hasFilterErrors = (errors: FilterErrors): boolean => Object.keys(errors).length > 0;

const numberAt = (filters: AudienceFilters, key: AudienceFilterKey): number | null => {
  const value = filters[key];
  return typeof value === 'number' ? value : null;
};
